import { useSeo } from "../hooks/useSeo";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowRight, ArrowUpRight } from "lucide-react";
import { useRevealAll } from "../hooks/useReveal";

export default function PhilosophyPage() {
  useSeo({
    title: "Filozófiánk – természetes haj, prémium minőség | Hair Elixir Hungary",
    description: "A Hair Elixir Hungary filozófiája: etikus forrásból származó, gondosan válogatott prémium emberi haj természetes hatású hajhosszabbításhoz.",
    path: "/filozofia",
  });
  const ref = useRef(null);
  useRevealAll(ref);

  const scrollTo = (id) => () => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main ref={ref} data-testid="page-philosophy" className="bg-[color:var(--he-bg)]">
      {/* === COVER === */}
      <section className="relative h-[82vh] min-h-[640px] bg-[color:var(--he-ink)] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/35250794/pexels-photo-35250794.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=2000"
            alt=""
            className="w-full h-full object-cover he-ken-burns"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/45 to-black/15" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/70" />
        </div>

        <div className="relative z-10 h-full grid grid-rows-[auto_1fr]">
          {/* TOP — masthead */}
          <div className="pt-28 md:pt-32">
            <div className="he-container">
              <div className="flex items-center justify-between text-white/85">
                <span className="overline-xs" style={{ letterSpacing: "0.34em" }}>
                  Filozófia
                </span>
                <span className="hidden sm:block h-px flex-1 mx-8 bg-[color:var(--he-gold)]/40" />
                <span className="overline-xs" style={{ letterSpacing: "0.34em" }}>
                  Hair Elixir · Budapest
                </span>
              </div>
            </div>
          </div>

          {/* MIDDLE — content, end-aligned */}
          <div className="flex items-end pb-16 md:pb-24">
            <div className="he-container w-full">
              <div className="grid grid-cols-12 gap-8 items-end">
                <div className="col-span-12 lg:col-span-9">
                  <div className="flex items-center gap-5 mb-10 he-rise" style={{ animationDelay: "200ms" }}>
                    <span className="block w-12 h-px bg-[color:var(--he-gold)]" />
                    <span
                      className="text-[color:var(--he-gold)] uppercase text-xs"
                      style={{ letterSpacing: "0.34em", fontWeight: 500 }}
                    >
                      Amiben hiszünk
                    </span>
                  </div>
                  <h1
                    className="font-serif-display text-white tracking-[-0.022em] he-rise"
                    style={{
                      animationDelay: "350ms",
                      fontWeight: 400,
                      fontSize: "clamp(2.8rem, 7.2vw, 7.2rem)",
                      lineHeight: 1.06,
                      textShadow: "0 4px 60px rgba(0,0,0,0.6)",
                    }}
                  >
                    Csendes{" "}
                    <span className="italic text-[color:var(--he-gold)]" style={{ fontWeight: 300 }}>
                      luxus.
                    </span>
                  </h1>
                  <p
                    className="mt-8 max-w-xl text-white/85 font-light text-base md:text-lg leading-[1.7] he-rise"
                    style={{ animationDelay: "550ms", textShadow: "0 2px 20px rgba(0,0,0,0.55)" }}
                  >
                    Három dolog vezet minket: tiszta származás, jól megválasztott
                    minőség és az, hogy a vásárlóinkkal hosszú távon dolgozzunk.
                  </p>
                  <button
                    onClick={scrollTo("manifest")}
                    className="mt-10 btn-arrow text-white he-rise"
                    style={{ animationDelay: "750ms" }}
                    data-testid="phil-cover-cta"
                  >
                    <span className="arrow-circle">
                    <ArrowDown size={18} className="arrow-icon text-white" />
                  </span>
                  <span style={{ fontWeight: 500 }}>Olvasd el</span>
                </button>
              </div>

              <div className="hidden lg:flex col-span-3 flex-col items-end gap-3 pb-2 he-rise" style={{ animationDelay: "900ms" }}>
                <span className="figure italic text-white/35 text-[5rem] leading-none">02</span>
                <span className="overline-xs text-white/65 text-right max-w-[200px]" style={{ fontSize: "0.6rem" }}>
                  Akik vagyunk, <br />
                  és amit nem csinálunk
                </span>
              </div>
            </div>
          </div>
          </div>
        </div>
      </section>

      {/* === I. MANIFEST === */}
      <section
        id="manifest"
        className="py-28 md:py-44 bg-[color:var(--he-bg)] relative overflow-hidden scroll-mt-24"
      >
        <div className="absolute right-0 -top-12 hidden md:block opacity-[0.04] pointer-events-none select-none">
          <span className="figure italic text-[28rem] leading-none text-[color:var(--he-ink)]">
            I.
          </span>
        </div>

        <div className="he-container relative">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 md:mb-20">
            <div className="md:col-span-4 reveal">
              <p className="overline mb-6 mag-marker">
                <span>I. fejezet · Akik vagyunk</span>
              </p>
              <span className="he-divider" />
            </div>
            <div className="md:col-span-7 md:col-start-6 reveal delay-1">
              <h2
                className="font-serif-display leading-[0.96] tracking-[-0.018em]"
                style={{ fontWeight: 400, fontSize: "clamp(2.4rem, 5.4vw, 5rem)" }}
              >
                Egyetlen célunk: <br />
                <span className="italic text-[color:var(--he-gold)]" style={{ fontWeight: 300 }}>
                  valódi haj, jól megválasztva.
                </span>
              </h2>
            </div>
          </div>

          {/* Big pull quote with drop cap */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-20 md:mb-24">
            <div className="md:col-span-2 reveal" />
            <div className="md:col-span-10">
              <p
                className="dropcap reveal delay-1 text-[color:var(--he-ink)] leading-[1.7] max-w-3xl"
                style={{ fontSize: "1.18rem", fontWeight: 300 }}
              >
                A Hair Elixir egy budapesti webshop, ahol európai és kazahsztáni
                forrásból származó, kutikula-irányított emberi hajat találsz.
                Magunk válogatjuk a tételeket, magunk csomagoljuk, és magyar
                nyelvű ügyfélszolgálattal segítünk a választásban.
              </p>
              <p
                className="mt-8 reveal delay-2 text-[color:var(--he-ink-soft)] leading-[1.85] max-w-2xl font-light"
                style={{ fontSize: "15px" }}
              >
                Nem dolgozunk influencer-kampányokkal és nem futtatunk
                folyamatos akciókat. Az ár tükrözi a valódi minőséget — és azt,
                hogy minden szál egy donortól származik, vegyítés nélkül.
              </p>

              {/* Signature */}
              <div className="mt-14 flex items-center gap-5 reveal delay-3">
                <span className="block w-16 h-px bg-[color:var(--he-gold)]" />
                <p
                  className="font-serif-display italic text-3xl text-[color:var(--he-gold)]"
                  style={{ fontWeight: 400 }}
                >
                  Hair Elixir
                </p>
                <span className="overline-xs text-[color:var(--he-ink-soft)]">
                  Budapest
                </span>
              </div>
            </div>
          </div>

          {/* Editorial spread image */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 reveal">
            <div className="md:col-span-7">
              <div className="he-img-wrap aspect-[16/10]">
                <img
                  src="https://images.unsplash.com/photo-1580618672591-eb180b1a973f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
                  alt="Hair Elixir — Filozófia"
                  className="he-img w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="md:col-span-4 md:col-start-9 flex flex-col justify-end">
              <span className="block w-10 h-px bg-[color:var(--he-gold)] mb-5" />
              <p
                className="font-serif-display italic text-2xl md:text-3xl leading-[1.2] text-[color:var(--he-ink)]"
                style={{ fontWeight: 400 }}
              >
                „A jó haj nem akarja, hogy észrevegyék."
              </p>
              <p className="mt-5 overline-xs text-[color:var(--he-ink-soft)]" style={{ fontSize: "0.6rem" }}>
                Hair Elixir
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* === II. PROCESS === */}
      <section className="py-28 md:py-40 bg-[color:var(--he-cream)] relative overflow-hidden">
        <div className="absolute left-0 top-12 hidden md:block opacity-[0.04] pointer-events-none select-none">
          <span className="figure italic text-[28rem] leading-none text-[color:var(--he-ink)]">
            II.
          </span>
        </div>

        <div className="he-container relative">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 md:mb-24 items-end">
            <div className="md:col-span-5 reveal">
              <p className="overline mb-6 mag-marker">
                <span>II. fejezet · Folyamat</span>
              </p>
              <h2
                className="font-serif-display leading-[0.96] tracking-[-0.018em]"
                style={{ fontWeight: 400, fontSize: "clamp(2.4rem, 5.4vw, 5rem)" }}
              >
                A donortól <br />
                <span className="italic text-[color:var(--he-gold)]" style={{ fontWeight: 300 }}>
                  a postáig.
                </span>
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8 reveal delay-1">
              <p className="text-[color:var(--he-ink-soft)] font-light leading-[1.75] text-[15px]">
                Négy lépéses folyamat, minden tételre ugyanaz. Nincs benne
                üzleti titok — csak emberi munka és figyelem.
              </p>
            </div>
          </div>

          {/* 4 step cards in horizontal row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            <Step
              num="01"
              title="Beszerzés"
              text="Egyetlen donortól vásároljuk a tételt — sosem keverünk két forrást. A származási helyet (európai vagy kazahsztáni) minden terméknél feltüntetjük."
              image="https://images.unsplash.com/photo-1761164920824-d18320030b41?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
            />
            <Step
              num="02"
              title="Előkészítés"
              text="A festett tételeket gyengéd festékkel, a természetes tételeket vegykezelés-mentesen készítjük el. Egyik sem agresszív, mindegyik megtartja a haj struktúráját."
              image="https://images.unsplash.com/photo-1555688739-3d25106bbccd?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
            />
            <Step
              num="03"
              title="Ellenőrzés"
              text="Minden copfot kézzel ellenőrzünk: vastagság, hosszeloszlás, fény, kutikulairány. Csak az kerül a katalógusba, amit magunknak is felvennénk."
              image="https://images.unsplash.com/photo-1754317397730-6a203d5a9f5d?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
            />
            <Step
              num="04"
              title="Csomagolás"
              text="Diszkrét, semmilyen márkajelzést nem viselő csomagolás. A küldeményen csak a feladó adatai szerepelnek — más semmi."
              image="https://images.unsplash.com/photo-1759157403273-2423c0f5718b?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
            />
          </div>
        </div>
      </section>

      {/* === III. PILLARS === */}
      <section className="py-28 md:py-44 bg-[color:var(--he-bg)] relative overflow-hidden">
        <div className="absolute right-0 top-12 hidden md:block opacity-[0.04] pointer-events-none select-none">
          <span className="figure italic text-[28rem] leading-none text-[color:var(--he-ink)]">
            III.
          </span>
        </div>

        <div className="he-container relative">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-20 items-end">
            <div className="md:col-span-7 reveal">
              <p className="overline mb-6 mag-marker">
                <span>III. fejezet · Amitől nem térünk el</span>
              </p>
              <h2
                className="font-serif-display leading-[0.96] tracking-[-0.018em]"
                style={{ fontWeight: 400, fontSize: "clamp(2.4rem, 5.6vw, 5.4rem)" }}
              >
                Három dolog, <br />
                <span className="italic text-[color:var(--he-gold)]" style={{ fontWeight: 300 }}>
                  amit megígérünk.
                </span>
              </h2>
            </div>
          </div>

          <div className="space-y-20 md:space-y-28">
            <Pillar
              num="01"
              title="Tiszta származás"
              meta="Európai vagy kazahsztáni — sosem keverve"
              text="Minden tételhez tudjuk, honnan származik. Egy copfban kizárólag egy donor haja van — ezért működik természetesen, és ezért hord olyan sokáig. Vegyített tételt nem árulunk."
              image="https://images.unsplash.com/photo-1612690207805-a3c172ea6e8c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
            />
            <Pillar
              num="02"
              title="Kutikula-irányított"
              meta="100% emberi haj, megtartott kutikula"
              text="A szálak ugyanabba az irányba állnak — pont úgy, ahogy a fejen nőttek. Ez az oka, hogy nem filcesedik, fésülhető, és hajvasalóval, sütővassal kezelhető, akárcsak a saját hajad."
              image="https://images.pexels.com/photos/35250794/pexels-photo-35250794.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1400"
              reversed
            />
            <Pillar
              num="03"
              title="Hosszú élettartam"
              meta="12–18 hónap rendszeres viseléssel"
              text="Megfelelő ápolással egy év biztosan, sok ügyfelünk 18 hónapot is kihord egy szettből. Minden megrendelés mellé adunk magyar nyelvű ápolási útmutatót, és e-mailben is válaszolunk, ha kérdés merül fel."
              image="https://images.unsplash.com/photo-1754317397730-6a203d5a9f5d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
            />
          </div>
        </div>
      </section>

      {/* === FOUNDER NOTE === */}
      <section className="py-24 md:py-36 bg-[color:var(--he-ink)] text-[color:var(--he-bg)] relative overflow-hidden border-y border-[color:var(--he-gold)]/30">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1761164920824-d18320030b41?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[color:var(--he-ink)] via-transparent to-[color:var(--he-ink)]" />
        </div>
        <div className="relative he-container">
          <div className="max-w-3xl mx-auto text-center">
            <p className="overline mb-7 text-[color:var(--he-gold)]">
              Üzenet az alapítótól
            </p>
            <p
              className="font-serif-display italic leading-[1.35] text-[color:var(--he-bg)]"
              style={{ fontWeight: 300, fontSize: "clamp(1.6rem, 3.4vw, 2.8rem)" }}
            >
              „Olyan webshopot építünk, ahonnan magunknak is rendelnénk.
              <br />
              Tisztességes ár, valódi minőség, magyar nyelvű támogatás."
            </p>
            <div className="mt-12 flex items-center justify-center gap-5">
              <span className="block w-12 h-px bg-[color:var(--he-gold)]" />
              <span
                className="font-serif-display italic text-2xl text-[color:var(--he-gold)]"
                style={{ fontWeight: 400 }}
              >
                Hair Elixir csapata
              </span>
              <span className="block w-12 h-px bg-[color:var(--he-gold)]" />
            </div>
            <p className="mt-4 overline-xs text-[color:var(--he-bg)]/55">
              Budapest
            </p>
          </div>
        </div>
      </section>

      {/* === FOOTER CTA === */}
      <section className="bg-[color:var(--he-cream)] py-24 md:py-36">
        <div className="he-container">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-7">
              <p className="overline mb-5">Folytatás</p>
              <h3
                className="font-serif-display leading-[1.0]"
                style={{ fontWeight: 400, fontSize: "clamp(2rem, 4.6vw, 3.8rem)" }}
              >
                Nézd meg a <span className="italic text-[color:var(--he-gold)]" style={{ fontWeight: 300 }}>termékeket.</span>
              </h3>
              <p className="mt-6 max-w-xl text-[color:var(--he-ink-soft)] font-light leading-relaxed">
                Az aktuálisan elérhető 11 tétel: konkrét hossz, súly és szín
                minden darabhoz. Ha tanácsra van szükséged, a választó pár perc.
              </p>
            </div>
            <div className="md:col-span-5 md:pl-8 flex flex-col sm:flex-row md:flex-col gap-5 md:items-end">
              <Link to="/termekek" className="btn-arrow text-[color:var(--he-ink)]" data-testid="phil-cta-products">
                <span className="arrow-circle">
                  <ArrowRight size={18} className="arrow-icon" />
                </span>
                <span style={{ fontWeight: 500 }}>Kollekció megtekintése</span>
              </Link>
              <Link to="/kerdoiv" className="btn-ghost text-[color:var(--he-ink)]" data-testid="phil-cta-quiz">
                <span>Melyik nekem való?</span>
                <ArrowUpRight size={14} className="ghost-arrow" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function Step({ num, title, text, image }) {
  return (
    <article className="reveal group">
      <div className="he-img-wrap aspect-[4/5] mb-6">
        <img src={image} alt={title} className="he-img w-full h-full object-cover" loading="lazy" />
        <span className="absolute top-5 left-5 figure italic text-white text-3xl drop-shadow">
          {num}
        </span>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-[color:var(--he-gold)]" />
      </div>
      <div className="flex items-baseline justify-between mb-3">
        <h3
          className="font-serif-display text-2xl md:text-[1.7rem]"
          style={{ fontWeight: 400 }}
        >
          {title}
        </h3>
        <span className="overline-xs text-[color:var(--he-gold)]">№ {num}</span>
      </div>
      <p className="text-sm text-[color:var(--he-ink-soft)] font-light leading-[1.75]">
        {text}
      </p>
    </article>
  );
}

function Pillar({ num, title, meta, text, image, reversed }) {
  return (
    <article className={`grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center ${reversed ? "md:[&>*:first-child]:order-2" : ""}`}>
      <div className="md:col-span-6 reveal">
        <div className="he-img-wrap aspect-[5/6]">
          <img src={image} alt={title} className="he-img w-full h-full object-cover" loading="lazy" />
          <span className="absolute top-5 left-5 figure italic text-white text-5xl drop-shadow">
            {num}
          </span>
        </div>
      </div>
      <div className="md:col-span-6 reveal delay-1">
        <p className="overline mb-5 mag-marker">
          <span>{meta}</span>
        </p>
        <h3
          className="font-serif-display leading-[0.98] tracking-[-0.012em]"
          style={{ fontWeight: 400, fontSize: "clamp(2.2rem, 4.4vw, 4rem)" }}
        >
          {title}
        </h3>
        <div className="my-7 he-divider" />
        <p className="text-[color:var(--he-ink-soft)] font-light leading-[1.85] max-w-lg">
          {text}
        </p>
      </div>
    </article>
  );
}
